import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
    useCreateAdminConversationFormQuery,
    useUpdateAdminConversationFormQuery,
} from '@entities/AdminConversationCategory';
import { IForm } from '@entities/AdminConversationCategory/model/types';
import { TAttachmentDTOCamelCased, TAttachmentOnClient } from '@entities/attachment/model/types';
import { getUpsertModalFormData } from '@features/AdminConversationCategoryUpsertModal/lib/helpers/getUpsertModalFormData';
import { useFormHandler } from '@features/AdminConversationCategoryUpsertModal/lib/hooks/useFormHandler';
import { DnDImageList } from '@features/AdminConversationCategoryUpsertModal/ui/DnDImageList';
import { CustomModal, DnD, Flex, Input, TextArea, TextEditor, UiButton } from '@shared/ui';

import cls from './NewsletterStep.module.css';

interface NewsletterStepProps {
    classes?: string;
    categoryId: number;
    onClose: () => void;
    formData?: IForm;
    formId?: number | null;
}

export const NewsletterStep = ({
    classes,
    categoryId,
    onClose,
    formData,
    formId,
}: NewsletterStepProps) => {
    const { t } = useTranslation();

    const {
        form,
        onClickInputRef,
        fileInputRef,
        onInputChange,
        uploadFilesHandler,
        onTextEditorChange,
    } = useFormHandler(formData);

    const { mutateAsync: mutateAsyncCreateAdminConversationForm } =
        useCreateAdminConversationFormQuery();
    const { mutateAsync: mutateAsyncUpdateAdminConversationForm } =
        useUpdateAdminConversationFormQuery();

    const isDisabledPrimaryButton =
        (form.primaryText && !form.primaryLink) || (!form.primaryText && form.primaryLink);

    const isDisabledSecondaryButton =
        (form.secondaryText && !form.secondaryLink) || (!form.secondaryText && form.secondaryLink);

    const isDisabled =
        !form.title || !form.description || isDisabledPrimaryButton || isDisabledSecondaryButton;

    const createNewsletter = async () => {
        const fd = getUpsertModalFormData(
            categoryId,
            form,
            'broadcast',
            !!formId,
            formData ? formData.actions : []
        );

        if (!formId) {
            await mutateAsyncCreateAdminConversationForm(fd);
        } else {
            await mutateAsyncUpdateAdminConversationForm({ formData: fd, id: formId });
        }
        onClose();
    };

    return (
        <>
            <CustomModal.Header classes={cn(cls.NewsletterStep, [classes])}>
                {t('NewsletterStep.return.CustomModal.Header')}
            </CustomModal.Header>
            <CustomModal.Body classes={cn(cls.NewsletterStepBody, [classes])}>
                <Flex gap={12} direction="column">
                    <TextArea
                        placeholder={t(
                            'NewsletterStep.return.CustomModal.Body.TextArea.placeholder'
                        )}
                        maxLength={100}
                        classes={cls.TextArea}
                        inputName="title"
                        onValueChange={onInputChange}
                        value={form.title}
                    />
                    <DnD<TAttachmentOnClient | TAttachmentDTOCamelCased>
                        attachmentsData={form.attachment}
                        multiple={false}
                        onUploadHandler={uploadFilesHandler}
                        fileInputRef={fileInputRef}
                        accept=".png, .jpeg, .gif, .jpg, .heic, .webp"
                        containerClassName={cls.dnd}
                        addFileTitle={t('NewsletterStep.return.CustomModal.Body.DnD.addFileTitle')}
                        renderAttachmentsList={() => (
                            <DnDImageList
                                onClickInputRef={onClickInputRef}
                                file={form.attachment[0]}
                            />
                        )}
                    />

                    <TextEditor
                        placeholder={t(
                            'NewsletterStep.return.CustomModal.Body.Input.description.placeholder'
                        )}
                        classes={cn(cls.TextArea, cls.description)}
                        inputName="description"
                        value={form.description}
                        maxLength={500}
                        onChange={onTextEditorChange}
                    />

                    <div className={cls.NewsletterMultiStepInputsRow}>
                        <div className={cn(cls.round, cls.primary)} />
                        <Input
                            maxLength={30}
                            name="primaryText"
                            onValueChange={onInputChange}
                            classes={[cls.input]}
                            value={form.primaryText}
                            placeholder={t(
                                'NewsletterStep.return.CustomModal.Body.Input.primaryText.placeholder'
                            )}
                        />
                        <Input
                            name="primaryLink"
                            onValueChange={onInputChange}
                            classes={[cls.input]}
                            value={form.primaryLink}
                            placeholder={t(
                                'NewsletterStep.return.CustomModal.Body.Input.primaryLink.placeholder'
                            )}
                        />
                    </div>
                    <div className={cls.NewsletterMultiStepInputsRow}>
                        <div className={cn(cls.round, cls.secondary)} />
                        <Input
                            maxLength={30}
                            name="secondaryText"
                            onValueChange={onInputChange}
                            classes={[cls.input]}
                            value={form.secondaryText}
                            placeholder={t(
                                'NewsletterStep.return.CustomModal.Body.Input.secondaryText.placeholder'
                            )}
                        />
                        <Input
                            name="secondaryLink"
                            onValueChange={onInputChange}
                            classes={[cls.input]}
                            value={form.secondaryLink}
                            placeholder={t(
                                'NewsletterStep.return.CustomModal.Body.Input.secondaryLink.placeholder'
                            )}
                        />
                    </div>
                    <TextArea
                        placeholder={t(
                            'SurveyStep.return.CustomModal.Body.TextArea.placeholder.comment'
                        )}
                        classes={cls.TextArea}
                        inputName="comment"
                        onValueChange={onInputChange}
                        value={form.comment}
                        maxLength={500}
                    />
                </Flex>
            </CustomModal.Body>
            <CustomModal.Footer classes={cls.footer}>
                <UiButton
                    onClick={onClose}
                    btnColor="secondary"
                    btnText={t('button.cancel.text')}
                />
                <UiButton
                    // @ts-ignore
                    disabled={isDisabled}
                    onClick={createNewsletter}
                    btnText={
                        formId
                            ? t('NewsletterStep.return.CustomModal.Footer.UiButton.btnText.edit')
                            : t('NewsletterStep.return.CustomModal.Footer.UiButton.btnText')
                    }
                />
            </CustomModal.Footer>
        </>
    );
};
